// Tela do Felipe na Visão Geral (#6504): Tecnologia e Marketing numa tela só (decisão
// dele, 27/10/2026). Abre no mês, com seletor.
//
//   - hoje: as minhas demandas, a fila sem responsável das duas áreas, o cronômetro e a
//     mídia de ontem;
//   - mês: o time de Tecnologia (no prazo, retrabalho, projetos) e o Marketing (custo por
//     contato, contratos de mídia, investimento);
//   - detalhe: o time por pessoa, as entregas por semana e os melhores criativos.
// Saúde do sistema (#6508) fica logo depois do "Hoje". Ferramentas e mídia contra o orçamento
// (#6509): ferramentas cadastradas aqui mesmo; verba e orçamento na aba Metas do Painel.
import React, { useMemo } from 'react';
import { BarChart2, FolderKanban, LifeBuoy, Sparkles } from 'lucide-react';
import { META_CUSTO_POR_CONTATO, entregasDoPeriodo } from '../../../src/lib/visaoGeral/tecnologia';
import { BlocosDoDia } from '../BlocosDoDia';
import { BlocoSaudeDoSistema } from '../BlocoSaudeDoSistema';
import {
  BarraCronometro, CartoesEntregas, CartoesMidiaDoMes, GraficoEntregasPorSemana, PainelCampanhas, PainelFilaSemResponsavel, fracaoDoMesAte,
  PainelMelhoresCriativos, TabelaTimeTecnologia, janelaDoMes, useMidiaDeOntem, useMidiaDoMes,
} from '../blocosTecnologiaMarketing';
import { CartaoQualidadeDoLead } from '../blocosComercialFase2';
import { PainelCustoFerramentasEMidia } from '../blocosFase3';
import { useMetasMensais } from '../../Dashboard/PainelExecutivo/sections/metas/useMetasMensais';
import { BotaoAcao, CabecalhoVisaoGeral, CartaoHoje, ListaAtalhos, SecaoVisaoGeral } from '../componentes';
import { useMinhasTarefas } from '../useMinhasTarefas';
import { soSolicitacoes } from '../../../src/lib/visaoGeral/minhasTarefas';
import { useDemandasConcluidas, useEquipeDoDepartamento, useFilaSemResponsavel } from '../useTecnologiaMarketing';
import { MinhaFila } from './TelaPadrao';
import { urgentesAbertas } from './TelaTecnologia';
import type { PropsTelaVisaoGeral } from '../tipos';

const reais = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataCurta = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

const TelaTecnologiaMarketing: React.FC<PropsTelaVisaoGeral> = ({ pessoa, souEu, definicao, periodo, aoMudarMes }) => {
  const j = useMemo(() => janelaDoMes(periodo.mes), [periodo.mes]);
  const { dados: todas } = useMinhasTarefas(pessoa.id);
  // Só as solicitações da Central: atividade do CRM (visita, ligação) não é demanda.
  const minhas = useMemo(() => (todas ? soSolicitacoes(todas) : null), [todas]);
  const fila = useFilaSemResponsavel(['Tecnologia', 'Marketing']);
  const equipe = useEquipeDoDepartamento('Tecnologia');
  const ids = useMemo(() => (equipe.dados ? equipe.dados.map(p => p.id) : null), [equipe.dados]);
  const demandas = useDemandasConcluidas(j.inicio, j.fim, ids);
  const doTime = useMemo(() => (demandas.dados ? entregasDoPeriodo(demandas.dados) : null), [demandas.dados]);
  const ontem = useMidiaDeOntem();
  const m = useMidiaDoMes(periodo.mes);
  const { metas } = useMetasMensais();
  const t = ontem.total;

  const acoes = souEu ? <BotaoAcao rotulo="Central de Solicitações" icone={LifeBuoy} para="/tarefas/solicitacoes" /> : null;

  return (
    <div className="space-y-8">
      <CabecalhoVisaoGeral nome={pessoa.full_name} pergunta={definicao.pergunta} periodo={periodo} aoMudarMes={aoMudarMes} acoes={acoes} />

      <BlocosDoDia pessoa={pessoa} perfil="tecnologia-marketing" />

      <SecaoVisaoGeral titulo="Hoje" subtitulo="minhas demandas, as filas e a mídia de ontem">
        <CartaoHoje valor={minhas ? minhas.atrasadas.length : null} rotulo="minhas demandas atrasadas" tom={minhas && minhas.atrasadas.length ? 'critico' : 'ok'} para="/tarefas/solicitacoes"
          dica={minhas ? `${minhas.hoje.length} vencem hoje e ${urgentesAbertas(minhas)} urgências abertas.` : undefined} />
        <CartaoHoje valor={minhas ? minhas.hoje.length : null} rotulo="minhas demandas vencem hoje" tom="atencao" para="/tarefas/solicitacoes" />
        <CartaoHoje valor={fila.dados ? fila.dados.length : null} rotulo="na fila sem responsável" tom="neutro" para="/tarefas/solicitacoes"
          dica={fila.dados ? `Tecnologia: ${fila.dados.filter(d => d.departamento_solicitacao === 'Tecnologia').length}; Marketing: ${fila.dados.filter(d => d.departamento_solicitacao === 'Marketing').length}.` : undefined} />
        <CartaoHoje valor={t ? t.contatos : null} rotulo={`contatos de mídia ontem (${dataCurta(ontem.ontem)})`}
          tom={t?.custo == null ? 'neutro' : t.custo <= META_CUSTO_POR_CONTATO ? 'ok' : 'atencao'} para="/marketing/campanhas"
          dica={t ? `Custo por contato ${t.custo == null ? '—' : reais(t.custo)} (meta até ${reais(META_CUSTO_POR_CONTATO)}).` : undefined} />
      </SecaoVisaoGeral>
      <BarraCronometro pessoaId={pessoa.id} />

      <BlocoSaudeDoSistema />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MinhaFila dados={minhas} titulo="Minhas demandas" />
        <PainelFilaSemResponsavel fila={fila.dados} titulo="Fila sem responsável" mostrarArea />
      </div>

      <PainelCampanhas titulo={`Mídia de ontem (${dataCurta(ontem.ontem)})`} total={t} campanhas={ontem.campanhas}
        vazio="Sem gasto em mídia importado para ontem. A importação do Meta roda uma vez por dia." />

      <SecaoVisaoGeral titulo="Mês · Tecnologia" subtitulo={`${j.rotulo}, demandas concluídas pelo time`} colunas={3}>
        <CartoesEntregas e={doTime} quem="pelo time" />
      </SecaoVisaoGeral>

      <SecaoVisaoGeral titulo="Mês · Marketing" subtitulo={`${m.j.rotulo}, Meta Ads e contratos`}>
        <CartoesMidiaDoMes m={m} qualidade={<CartaoQualidadeDoLead mes={periodo.mes} corrente={periodo.corrente} />} />
      </SecaoVisaoGeral>

      <section className="space-y-3" aria-label="Detalhe">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Detalhe</h2>
        <TabelaTimeTecnologia equipe={equipe.dados} demandas={demandas.dados} />
        <PainelCustoFerramentasEMidia mes={periodo.mes} rotuloMes={m.j.rotulo} gastoMidia={m.atual ? m.atual.gasto : null}
          fracao={fracaoDoMesAte(m.j.primeiroDia, m.j.ultimoDia)} metas={metas} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <GraficoEntregasPorSemana demandas={demandas.dados} titulo="Entregas do time por semana" />
          <PainelMelhoresCriativos criativos={m.criativos} rotuloMes={m.j.rotulo} />
        </div>
      </section>

      <ListaAtalhos itens={[
        { rotulo: 'Central de Solicitações', para: '/tarefas/solicitacoes', icone: LifeBuoy },
        { rotulo: 'Projetos', para: '/projetos', icone: FolderKanban },
        { rotulo: 'Campanhas', para: '/marketing/campanhas', icone: BarChart2 },
        { rotulo: 'Criativos', para: '/marketing/criativos', icone: Sparkles },
      ]} />
    </div>
  );
};

export default TelaTecnologiaMarketing;
